import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface UnifiedUser {
  id: string;
  email: string;
  user_type: 'crm' | 'portal';
  roles: string[];
  tenant_id: string | null;
  tenant_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  meta: any;
}

export const useUnifiedUsers = () => {
  const [users, setUsers] = useState<UnifiedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      console.log('[UNIFIED] Fetching all users...');
      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { action: 'list-all-users' }
      });

      if (error) throw error;

      setUsers(data.users || []);
      console.log('[UNIFIED] Users loaded:', data.users?.length || 0);
    } catch (error: any) {
      console.error('[UNIFIED] Error fetching users:', error);
      toast({
        title: 'Fout bij laden gebruikers',
        description: error.message || 'Er ging iets mis.',
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  const createChargingUser = async (email: string, password: string, displayName?: string) => {
    try {
      console.log('[UNIFIED] Creating charging user:', { email, displayName });
      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { 
          action: 'create-charging-user',
          email,
          password,
          displayName
        }
      });

      if (error) throw error;

      toast({
        title: 'Charging gebruiker aangemaakt',
        description: `${email} is aangemaakt met charging_user rol.`,
      });

      await fetchAllUsers();
      return true;
    } catch (error: any) {
      console.error('[UNIFIED] Error creating charging user:', error);
      toast({
        title: 'Fout bij aanmaken',
        description: error.message || 'Er ging iets mis bij het aanmaken.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const setUserTenant = async (userId: string, tenantId: string) => {
    try {
      const { error } = await supabase.functions.invoke('admin-users', {
        body: { 
          action: 'set-user-tenant',
          userId,
          tenantId
        }
      });

      if (error) throw error;

      toast({
        title: 'Tenant ingesteld',
        description: 'De tenant is succesvol toegewezen.',
      });

      await fetchAllUsers();
      return true;
    } catch (error: any) {
      console.error('[UNIFIED] Error setting tenant:', error);
      toast({
        title: 'Fout bij tenant instellen',
        description: error.message || 'Er ging iets mis.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const togglePortalUserActive = async (userId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('portal_users')
        .update({ is_active: !isActive })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: isActive ? 'Gebruiker gedeactiveerd' : 'Gebruiker geactiveerd',
        description: 'Status succesvol bijgewerkt.',
      });

      await fetchAllUsers();
      return true;
    } catch (error: any) {
      console.error('[UNIFIED] Error toggling user active:', error);
      toast({
        title: 'Fout bij status wijzigen',
        description: error.message || 'Er ging iets mis.',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    users,
    loading,
    fetchAllUsers,
    createChargingUser,
    setUserTenant,
    togglePortalUserActive
  };
};
